import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useEnableDepartments -- shared read of the SuperAdmin
 * ``enable_departments`` platform-settings toggle.
 *
 * Departments is a broad feature: the Client Portal Budget page, the
 * distributor client-departments panel, the store<->department mapping
 * panel, and the storefront checkout picker. Rather than sprinkle flag
 * checks across those ~78 files (which would violate
 * ``docs/design-philosophy.md`` -- "one mechanism per concept"), the flag
 * has exactly TWO read mechanisms in the whole product:
 *
 *   1. THIS composable, for surfaces the frontend owns outright (nav
 *      entries and distributor-side panels).
 *   2. The backend emitting ``client_departments: []``, which the three
 *      storefront call sites already funnel through
 *      ``departmentPickerMode()`` in ``@/utils/campaign``. That single
 *      cascade hides the checkout picker AND relaxes the submit-blocking
 *      guard without either file learning the flag exists.
 *
 * Fail-SAFE default TRUE -- the OPPOSITE of useEnableInlineImages, and the
 * direction is load-bearing. Departments are the spine of the Client
 * Portal budget model (``budgets.client_department_id`` is NOT NULL) and
 * an N-department store's checkout REQUIRES a shopper pick, so a
 * transient read failure must never HIDE a configured store's surfaces:
 * hiding on uncertainty could strand a checkout, while showing on
 * uncertainty is merely a cosmetic surprise.
 *
 * Public surface:
 *
 *   const { enableDepartments, loading, error } = useEnableDepartments()
 *
 *   enableDepartments: Ref<boolean>   // TRUE = show Departments surfaces
 *   loading:           Ref<boolean>   // request in flight
 *   error:             Ref<Error|null> // any fetch error (fails safe TRUE)
 */
// Use the /api-prefixed form to match the peer call sites for the other
// platform-settings reads (PlatformSettings.vue included).
const ENDPOINT = '/api/platform-settings/enable-departments'

// Module-level shared state: one fetch per page load, shared across every
// caller. ``_value`` holds the last resolved boolean (or the TRUE
// fail-safe default until/unless a real fetch resolves it).
const _value = ref(true)
const _loading = ref(false)
const _error = ref(null)
let _inflight = null
let _resolved = false

function _fetchOnce() {
    if (_resolved) {
        return Promise.resolve(_value.value)
    }
    if (_inflight) {
        return _inflight
    }
    _loading.value = true
    _error.value = null
    _inflight = axiosInstance
        .get(ENDPOINT)
        .then((resp) => {
            const raw = resp && resp.data ? resp.data.value : null
            // Only a real boolean overrides the fail-safe default. Any
            // other shape (missing key, string, number, null) leaves it
            // TRUE -- never coerce a truthy/falsy non-bool into hiding a
            // configured store's surfaces.
            if (typeof raw === 'boolean') {
                _value.value = raw
            } else {
                _value.value = true
            }
            _resolved = true
            return _value.value
        })
        .catch((err) => {
            // Fail SAFE: keep departments VISIBLE on a read error. Do NOT
            // mark resolved so a later mount can retry the fetch.
            _error.value = err
            _value.value = true
            return _value.value
        })
        .finally(() => {
            _loading.value = false
            _inflight = null
        })
    return _inflight
}

/**
 * Test-only: reset the module-level shared state so specs re-isolate per
 * test without a prior fetch bleeding across cases.
 */
export function _resetEnableDepartmentsCache() {
    _value.value = true
    _loading.value = false
    _error.value = null
    _inflight = null
    _resolved = false
}

export function useEnableDepartments() {
    // Kick the shared fetch on first use; subsequent callers reuse it.
    _fetchOnce()
    return {
        enableDepartments: _value,
        loading: _loading,
        error: _error,
    }
}

export default useEnableDepartments
